import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Pencil, Phone, Mail, MapPin, CalendarPlus, BellPlus,
  MessageCircle, CalendarClock, Flag, CircleDot, Clock, ExternalLink, PhoneCall, Headset,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtDate, fmtDateTime, apptMeta, convoStatusMeta, followupStatusMeta } from "@/config/crm";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { AppointmentFormDialog } from "@/components/appointments/AppointmentFormDialog";
import { FollowUpDialog } from "@/components/followups/FollowUpDialog";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div className="min-w-0"><p className="text-xs uppercase tracking-wider text-slate-400">{label}</p><p className="text-sm text-slate-800 break-words">{value || "—"}</p></div>
  </div>
);

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/patients/${id}/overview`); setData(data); }
    catch { toast.error("Patient not found"); navigate("/patients"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); api.get("/leads/owners").then(({ data }) => setOwners(data.owners)).catch(() => {}); }, [load]);

  if (loading || !data) return (
    <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-96 lg:col-span-2" /><Skeleton className="h-96" /></div></div>
  );

  const p = data.patient;
  const lead = data.lead;

  return (
    <div className="animate-fade-up space-y-6" data-testid="patient-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/patients")} data-testid="back-to-patients"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="patient-detail-name">{p.full_name}</h1>
              <Badge className="bg-slate-100 text-slate-600 font-mono">{p.patient_code}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-500">{p.phone}{p.lead_source ? ` · from ${p.lead_source}` : ""}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setFuOpen(true)} data-testid="patient-add-followup"><BellPlus className="h-4 w-4" /> Follow-up</Button>
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="patient-edit"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setApptOpen(true)} data-testid="patient-book-appointment"><CalendarPlus className="h-4 w-4" /> Book Appointment</Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Overview</CardTitle></CardHeader>
            <CardContent>
              <Tabs defaultValue="contact">
                <TabsList className="flex-wrap">
                  <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
                  <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments ({data.appointments.length})</TabsTrigger>
                  <TabsTrigger value="conversations" data-testid="tab-conversations">Conversations ({data.conversations.length})</TabsTrigger>
                  <TabsTrigger value="calls" data-testid="tab-calls">Calls ({(data.calls || []).length})</TabsTrigger>
                  <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups ({data.followups.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="contact" className="mt-4">
                  <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    <InfoRow icon={Phone} label="Phone" value={p.phone} />
                    <InfoRow icon={MessageCircle} label="WhatsApp" value={p.whatsapp} />
                    <InfoRow icon={Mail} label="Email" value={p.email} />
                    <InfoRow icon={CircleDot} label="Gender / DOB" value={[p.gender, p.dob].filter(Boolean).join(" · ")} />
                    <InfoRow icon={MapPin} label="City" value={[p.address, p.city].filter(Boolean).join(", ")} />
                    <InfoRow icon={CircleDot} label="Preferred language" value={p.preferred_language} />
                    <InfoRow icon={CircleDot} label="Insurance" value={[p.insurance_provider, p.insurance_id].filter(Boolean).join(" · ")} />
                    <InfoRow icon={CircleDot} label="Owner" value={p.owner_name} />
                  </div>
                  {p.notes && <><Separator className="my-3" /><p className="whitespace-pre-wrap text-sm text-slate-700">{p.notes}</p></>}
                </TabsContent>

                <TabsContent value="appointments" className="mt-4 space-y-2">
                  {data.appointments.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No appointments.</p>
                    : data.appointments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3" data-testid="patient-appt-item">
                        <div><p className="text-sm font-medium text-slate-900">{a.service_name} · {a.doctor_name}</p><p className="text-xs text-slate-500">{a.date} at {a.start_time}</p></div>
                        <Badge className={cn(apptMeta(a.status).badge)}>{apptMeta(a.status).label}</Badge>
                      </div>
                    ))}
                </TabsContent>

                <TabsContent value="conversations" className="mt-4 space-y-2">
                  {data.conversations.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No conversations.</p>
                    : data.conversations.map((c) => (
                      <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/conversations?open=${c.id}`)} data-testid="patient-convo-item">
                        <div><p className="text-sm font-medium text-slate-900">{c.channel}</p><p className="text-xs text-slate-500 truncate max-w-xs">{c.last_message_preview || "No messages"}</p></div>
                        <Badge className={cn(convoStatusMeta(c.status).badge)}>{convoStatusMeta(c.status).label}</Badge>
                      </div>
                    ))}
                </TabsContent>

                <TabsContent value="calls" className="mt-4 space-y-2">
                  {(data.calls || []).length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No voice calls.</p>
                    : data.calls.map((c) => (
                      <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/calls/${c.id}`)} data-testid="patient-call-item">
                        <div className="flex items-center gap-2">
                          <PhoneCall className="h-4 w-4 text-emerald-600" />
                          <div>
                            <p className="text-sm font-medium text-slate-900 capitalize">{c.direction} call · {(c.intent || "enquiry").replace(/_/g, " ")}</p>
                            <p className="text-xs text-slate-500">{fmtDateTime(c.start_time)}</p>
                          </div>
                        </div>
                        {c.handoff_requested && <Badge className="bg-amber-50 text-amber-700"><Headset className="mr-1 h-3 w-3" />Handoff</Badge>}
                      </div>
                    ))}
                </TabsContent>

                <TabsContent value="followups" className="mt-4 space-y-2">
                  {data.followups.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No follow-ups.</p>
                    : data.followups.map((f) => (
                      <div key={f.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3" data-testid="patient-fu-item">
                        <div><p className="text-sm font-medium text-slate-900">{f.type}</p><p className="text-xs text-slate-500">Due {fmtDateTime(f.due_at)}</p></div>
                        <Badge className={cn(f.is_overdue ? "bg-red-50 text-red-600" : followupStatusMeta(f.status).badge)}>{f.is_overdue ? "Overdue" : followupStatusMeta(f.status).label}</Badge>
                      </div>
                    ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card className="shadow-sm" data-testid="patient-timeline">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Activity Timeline</CardTitle></CardHeader>
            <CardContent>
              {data.timeline.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p>
                : <div className="space-y-4">
                  {data.timeline.map((a, i) => (
                    <div key={a.id} className="flex gap-3" data-testid="patient-activity-item">
                      <div className="flex flex-col items-center">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Clock className="h-4 w-4" /></div>
                        {i < data.timeline.length - 1 && <div className="my-1 w-px flex-1 bg-slate-200" />}
                      </div>
                      <div className="pb-1"><p className="text-sm text-slate-800">{a.message}</p><p className="text-xs text-slate-400">{fmtDateTime(a.at)}{a.by ? ` · ${a.by}` : ""}</p></div>
                    </div>
                  ))}
                </div>}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Lead / Source</CardTitle></CardHeader>
            <CardContent>
              {lead ? (
                <div className="space-y-3">
                  <InfoRow icon={Flag} label="Originating lead" value={lead.full_name} />
                  <InfoRow icon={CircleDot} label="Source / Campaign" value={[lead.source, lead.campaign].filter(Boolean).join(" · ")} />
                  <InfoRow icon={CircleDot} label="Interest" value={lead.interest} />
                  <Button variant="outline" className="w-full" onClick={() => navigate(`/leads/${lead.id}`)} data-testid="view-originating-lead">
                    <ExternalLink className="h-4 w-4" /> View Lead
                  </Button>
                </div>
              ) : <p className="text-sm text-slate-400">Direct patient (no originating lead).</p>}
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="space-y-2 pt-6">
              <p className="text-xs text-slate-400">Created</p><p className="text-sm text-slate-700">{fmtDate(p.created_at)}</p>
              <p className="text-xs text-slate-400">Last updated</p><p className="text-sm text-slate-700">{fmtDate(p.updated_at)}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <PatientFormDialog open={editOpen} onOpenChange={setEditOpen} patient={p} owners={owners} onSaved={load} />
      <AppointmentFormDialog open={apptOpen} onOpenChange={setApptOpen} presetPatientId={p.id} presetPatientName={p.full_name} presetLeadId={p.lead_id} onBooked={load} />
      <FollowUpDialog open={fuOpen} onOpenChange={setFuOpen} preset={{ patient_id: p.id, lead_id: p.lead_id }} owners={owners} onSaved={load} />
    </div>
  );
}
